<template>
  <MDBSideNav right v-model="viewProjectListDrawer" mode="push" contentSelector="#ProjectTagging" :sidenavWidth="354">
    <div class="report-select-employee-container">
      <div class="header">
        <IconArrowLeft class="icon" @click="handleAllDrawerClose(false)" />
        <h6>Select Project</h6>
        <IconX class="icon x" @click="handleAllDrawerClose(true)" />
      </div>
      <div class="filter">
        <SPASearchbar v-model.trim="reportSelectProjectSearchString" @keypress.stop="handleKeyPress" @clickSearch="handleSearch" placeholder="Search" />
      </div>
      <div class="employee-list" :class="{ 'is-loading-full': isLoadingFull, 'is-loading-scroll': isLoadingScroll }">
        <MDBScrollbar ref="projectListDOM">
          <template v-if="noMatchingSearch && !isLoading">
            <div class="empty-container">
              <div>
                <h5>No Results Found</h5>
              </div>
            </div>
          </template>
          <template v-else-if="isProjectListEmpty && !isLoading">
            <div class="empty-container">
              <img src="@/assets/images/2024/no-projects.png" alt="no-projects" />
              <div>
                <h5>No Projects Created</h5>
                To start off with the module, go to Project List and select the “Create a Project” button!
              </div>
            </div>
          </template>
          <template v-for="(group, key) in groupedProjects" :key="key" v-else>
            <div class="group-header">{{ key }}</div>
            <template v-for="project in group" :key="project.projectId">
              <div class="list-row" @click="selectProject(project)" :class="{ selected: findProjectFromSelection(project) }">
                <div class="list-row-container text-truncate">
                  <div class="fullname text-truncate">{{ project.name }}</div>
                  <div class="employee-id-number">{{ project.code }}</div>
                </div>
                <IconCheck v-if="findProjectFromSelection(project)" class="list-row-icon" />
              </div>
            </template>
          </template>
          <MDBSpinner v-if="isLoading" />
        </MDBScrollbar>
      </div>
      <div class="action">
        <SPAButton color="success" :ripple="false" @click.stop.prevent="confirmSelection" :disabled="isSelectedProjectEmpty"> Confirm </SPAButton>
      </div>
    </div>
  </MDBSideNav>
</template>

<script>
import { MDBSideNav, MDBScrollbar, MDBSpinner } from 'mdb-vue-ui-kit';
import { storeToRefs } from 'pinia';
import { useProjectTaggingStore } from '../../../../stores/projectTagging';
import IconArrowLeft from '~icons/ph/arrow-left';
import IconX from '~icons/ph/x';
import IconCheck from '~icons/ph/check';
import { ref, computed, watch } from 'vue';
import SPASearchbar from '../../../../components/SPASearchbar.vue';
import { useInfiniteScroll, useDebounceFn } from '@vueuse/core';
import { GetProjects } from '../../../../services/ProjectTagging/GetProjects';
import SPAButton from '../../../../components/SPAButton.vue';
export default {
  name: 'ProjectTaggingReportSelectEmployee',
  components: {
    MDBSideNav,
    IconArrowLeft,
    IconX,
    SPASearchbar,
    MDBScrollbar,
    MDBSpinner,
    IconCheck,
    SPAButton,
  },
  setup() {
    const projectTaggingStore = useProjectTaggingStore();
    const { viewProjectListDrawer, drawerReportState, searchFilter, confirmedProject } = storeToRefs(projectTaggingStore);
    const reportSelectProjectSearchString = ref('');
    const projectList = ref([]);
    const currentPage = ref(1);
    const totalItems = ref(0);
    const totalPages = ref(0);
    const isLoading = ref(true);
    const selectedProject = ref({});
    const defaultParams = { SortColumn: 'Name', SortOrder: 'asc', RowsPerPage: 15 };
    const groupedProjects = computed(() => {
      return projectList.value.reduce((groups, project) => {
        let firstLetter = project.name.charAt(0).toUpperCase();
        if (!groups[firstLetter]) {
          groups[firstLetter] = [];
        }
        let entry = groups[firstLetter];
        entry.push(project);
        return groups;
      }, {});
    });
    const isSelectedProjectEmpty = computed(() => Object.keys(selectedProject.value).length < 1);
    const isLoadingFull = computed(() => isLoading.value && projectList.value.length === 0);
    const isLoadingScroll = computed(() => isLoading.value && projectList.value.length > 0);
    const noMatchingSearch = ref(false);
    const handleSearch = async () => {
      isLoading.value = true;
      projectList.value = [];
      totalItems.value = 0;
      const params = {
        SearchString: reportSelectProjectSearchString.value,
        FilterBy: searchFilter.value,
        ...defaultParams,
      };
      const result = await GetProjects(params);
      currentPage.value = 1;
      projectList.value = result.data;
      totalItems.value = result.pagination.totalItems;
      isLoading.value = false;
      noMatchingSearch.value = reportSelectProjectSearchString.value != '' && projectList.value.length < 1 ? true : false;
    };
    const handleKeyPress = () => {
      isLoading.value = true;
      debouncedSearch();
    };
    const debouncedSearch = useDebounceFn(() => {
      handleSearch();
    }, 1000);

    const confirmSelection = () => {
      confirmedProject.value = selectedProject.value;
      viewProjectListDrawer.value = false;
    };
    const resetSelection = () => {
      selectedProject.value = [];
    };
    const selectProject = (project) => {
      const found = findProjectFromSelection(project);
      if (!found) selectedProject.value = project;
    };
    const findProjectFromSelection = (project) => {
      return selectedProject.value.id === project.id;
    };

    const projectListDOM = ref();
    useInfiniteScroll(
      projectListDOM,
      async () => {
        if (currentPage.value === totalPages.value) {
          return;
        }
        isLoading.value = true;
        currentPage.value += 1;
        const params = {
          ...defaultParams,
          ...{ PageNumber: currentPage.value },
        };
        const result = await GetProjects(params);
        projectList.value = projectList.value.concat(result.data);
        totalItems.value = result.pagination.totalItems;
        currentPage.value = result.pagination.currentPage;
        isLoading.value = false;
      },
      { distance: 10 }
    );
    const handleAllDrawerClose = (closeAll) => {
      if (!closeAll) {
        viewProjectListDrawer.value = false;
        return;
      }
      drawerReportState.value = false;
      viewProjectListDrawer.value = false;
    };

    const isProjectListEmpty = computed(() => projectList.value.length < 1);

    watch(viewProjectListDrawer, async (newStatus) => {
      if (newStatus) {
        isLoading.value = true;
        selectedProject.value = confirmedProject.value;
        const result = await GetProjects(defaultParams);
        projectList.value = result.data;
        totalItems.value = result.pagination.totalItems;
        totalPages.value = result.pagination.totalPages;
        currentPage.value = result.pagination.currentPage;
        isLoading.value = false;
      }
    });

    return {
      viewProjectListDrawer,
      reportSelectProjectSearchString,
      handleSearch,
      confirmSelection,
      groupedProjects,
      isLoading,
      resetSelection,
      projectListDOM,
      isLoadingFull,
      isLoadingScroll,
      selectedProject,
      selectProject,
      findProjectFromSelection,
      handleAllDrawerClose,
      isSelectedProjectEmpty,
      isProjectListEmpty,
      handleKeyPress,
      noMatchingSearch,
    };
  },
};
</script>

<style lang="scss" scoped>
.report-select-employee-container {
  display: grid;
  line-height: 24px;
  flex-direction: column;
  grid-template-rows: auto auto 1fr auto;
  height: 100%;
}
.header,
.filter,
.selection-info,
.employee-list,
.action {
  padding: 14px 16px;
  border: 1px solid $neutral-40;
  border-right: 0;
  border-top: 0;
}
.header {
  display: flex;
  align-items: center;
  gap: 10px;

  h6 {
    margin: 0;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    color: $neutral-800;
  }
  .icon {
    width: 16px;
    height: 16px;
    cursor: pointer;

    &:hover {
      color: $green-300;
    }
  }
  .x {
    margin-left: auto;
  }
}
.selection-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  .selected-counter {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    font-weight: 500;
    font-size: 12px;

    span {
      color: $neutral-400;
      line-height: 12px;
      letter-spacing: 0.04em;
    }
    .spa-lozenge {
      display: inline-flex;
      align-items: center;
      background-color: $blue-300;
      color: $neutral-0;
      pointer-events: none;
      padding-inline: 12px;
      border-radius: 12px;
      line-height: 16px;
    }
  }
  .selection-reset {
    font-weight: 500;
    font-size: 12px;
    padding: 10px 12px;
    line-height: 12px;
    color: $neutral-300;
    cursor: pointer;
  }
}
.employee-list {
  overflow-y: auto;
  padding-inline: 0;

  &.is-loading-full .ps {
    display: grid;
    place-content: center;
  }
  &.is-loading-scroll .spinner-border {
    margin-block: 10px;
    margin-inline: auto;
    display: block;
  }
  .group-header {
    padding-inline: 16px;
    font-size: 12px;
    color: $neutral-200;
  }
  .spinner-border {
    border-color: $neutral-300;
    border-right-color: transparent;
  }
  .list-row {
    display: grid;
    grid-template-columns: 3fr auto;
    align-items: center;
    padding-inline: 16px;

    &:hover,
    &.selected {
      background-color: $green-50;
      cursor: pointer;
    }

    .list-row-container {
      padding: 12px 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .list-row-icon {
      color: $green-300;
    }
  }
  .fullname {
    color: $neutral-800;
  }
  .employee-id-number {
    color: $neutral-400;
    font-size: 12px;
  }
  .members-status {
    font-size: 12px;
    line-height: 16px;
    text-transform: capitalize;
    color: $neutral-400;
  }
}
.action button {
  width: 100%;
}
.empty-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  padding: 24px;
  text-align: center;
  div {
    display: grid;
    justify-items: center;
    line-height: 24px;
    font-size: 14px;
    h5 {
      font-weight: 500;
    }
  }
}
</style>
